import React, { useState } from 'react';
import { ChevronDown, ChevronRight, FileText } from 'lucide-react';
import { DocumentItem } from './DocumentItem';
import type { DocumentCategory as DocumentCategoryType, ProjectDocument, DocumentStatus } from '../../types/documents';

interface DocumentCategoryProps {
  category: DocumentCategoryType;
  documents: ProjectDocument[];
  projectId?: string;
  projectOwnerId?: string;
  projectName?: string;
  onUpload: (categoryCode: string, key: string, file: File) => Promise<void>;
  onDelete: (documentId: string) => Promise<void>;
  onVerify: (documentId: string, status: DocumentStatus, note?: string) => Promise<void>;
  isUploading?: boolean;
  canManage?: boolean;
  showOnlyMissing?: boolean;
}

export const DocumentCategory: React.FC<DocumentCategoryProps> = ({
  category,
  documents,
  projectId,
  projectOwnerId,
  projectName,
  onUpload,
  onDelete,
  onVerify,
  isUploading = false,
  canManage = false,
  showOnlyMissing = false
}) => {
  const [isExpanded, setIsExpanded] = useState(true);

  // Calculate completion stats for this category
  const categoryDocuments = documents.filter(doc => doc.category === category.code);
  const requiredItems = category.items.filter(item => item.required && !item.conditional);
  const completedRequired = requiredItems.filter(item => {
    const doc = categoryDocuments.find(d => d.key === item.key);
    return doc && ['UPLOADED', 'VERIFIED'].includes(doc.status);
  }).length;

  // Filter items based on showOnlyMissing
  const filteredItems = category.items.filter(item => {
    if (!showOnlyMissing) return true;
    const doc = categoryDocuments.find(d => d.key === item.key);
    return !doc || doc.status === 'MISSING';
  });

  if (showOnlyMissing && filteredItems.length === 0) {
    return null;
  }

  const handleUpload = async (key: string, file: File) => {
    await onUpload(category.code, key, file);
  };

  const handleDelete = async (key: string) => {
    const doc = categoryDocuments.find(d => d.key === key);
    if (doc) {
      await onDelete(doc.id);
    }
  };

  const handleVerify = async (key: string, status: DocumentStatus, note?: string) => {
    const doc = categoryDocuments.find(d => d.key === key);
    if (doc) {
      await onVerify(doc.id, status, note);
    }
  };

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-100">
      {/* Category Header */}
      <div 
        className="flex items-center justify-between p-4 cursor-pointer hover:bg-gray-50 transition-colors"
        onClick={() => setIsExpanded(!isExpanded)}
      >
        <div className="flex items-center space-x-3">
          {isExpanded ? (
            <ChevronDown className="w-5 h-5 text-gray-400" />
          ) : (
            <ChevronRight className="w-5 h-5 text-gray-400" />
          )}
          <FileText className="w-5 h-5 text-blue-600" />
          <div>
            <h3 className="font-semibold text-gray-900">{category.label}</h3>
            {category.required && (
              <p className="text-sm text-gray-600">
                {completedRequired}/{requiredItems.length} documents requis complétés
              </p>
            )}
          </div>
        </div>
        
        <div className="flex items-center space-x-2">
          {category.required && (
            <span className="px-2 py-1 bg-red-100 text-red-800 text-xs font-medium rounded-full">
              Obligatoire
            </span>
          )}
          <span className="text-sm text-gray-500">
            {filteredItems.length} document{filteredItems.length > 1 ? 's' : ''}
          </span>
        </div>
      </div>

      {/* Category Content */}
      {isExpanded && (
        <div className="border-t border-gray-100 p-4 space-y-4">
          {filteredItems.map((item) => {
            const projectDocument = categoryDocuments.find(doc => doc.key === item.key);
            
            return (
              <DocumentItem
                key={item.key}
                documentItem={item}
                projectDocument={projectDocument}
                projectId={projectId}
                projectOwnerId={projectOwnerId}
                projectName={projectName}
                onUpload={(file) => handleUpload(item.key, file)}
                onDelete={() => handleDelete(item.key)}
                onVerify={(status, note) => handleVerify(item.key, status, note)}
                isUploading={isUploading}
                canManage={canManage}
              />
            );
          })}
        </div>
      )}
    </div>
  );
};